/**
 * File Upload Security Service
 * Resolves CWE-434: Unrestricted Upload of File with Dangerous Type
 * Implements comprehensive file validation and security controls
 */

class FileUploadSecurityService {
  constructor() {
    // Whitelist of allowed MIME types (secure by default)
    this.allowedMimeTypes = new Set([
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp',
      // Documents  
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'text/csv'
    ]);

    // Whitelist of allowed file extensions (double validation)
    this.allowedExtensions = new Set([
      'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp',
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'
    ]);

    // Dangerous file extensions (blacklist for additional security)
    this.dangerousExtensions = new Set([
      'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar', 'app', 'deb', 'pkg', 'dmg',
      'php', 'jsp', 'asp', 'aspx', 'sh', 'bash', 'ps1', 'py', 'rb', 'pl', 'cgi',
      'htm', 'html', 'svg', 'xml', 'swf', 'msi', 'dll', 'so', 'dylib'
    ]);

    // Magic number signatures for file type verification
    this.magicNumbers = {
      // Images
      'image/jpeg': [[0xFF, 0xD8, 0xFF]],
      'image/png': [[0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]],
      'image/gif': [[0x47, 0x49, 0x46, 0x38, 0x37, 0x61], [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]],
      'image/webp': [[0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50]],
      'image/bmp': [[0x42, 0x4D]],
      // Documents
      'application/pdf': [[0x25, 0x50, 0x44, 0x46]],
      'application/zip': [[0x50, 0x4B, 0x03, 0x04], [0x50, 0x4B, 0x05, 0x06], [0x50, 0x4B, 0x07, 0x08]],
      // Office documents (ZIP-based)
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [[0x50, 0x4B, 0x03, 0x04]],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [[0x50, 0x4B, 0x03, 0x04]],
    };

    // Maximum file sizes by type (in bytes) - 1MB limit for all types
    this.maxFileSizes = {
      'image/jpeg': 1 * 1024 * 1024,
      'image/png': 1 * 1024 * 1024,
      'image/gif': 1 * 1024 * 1024,
      'image/webp': 1 * 1024 * 1024,
      'image/bmp': 1 * 1024 * 1024,
      'application/pdf': 1 * 1024 * 1024,
      'application/msword': 1 * 1024 * 1024,
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 1 * 1024 * 1024,
      'application/vnd.ms-excel': 1 * 1024 * 1024,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 1 * 1024 * 1024,
      'text/plain': 1 * 1024 * 1024,
      'text/csv': 1 * 1024 * 1024
    };
  }

  /**
   * Comprehensive file validation
   * @param {File} file - The file to validate
   * @returns {Promise<Object>} - Validation result with success/error details
   */
  async validateFile(file) {
    const result = {
      isValid: false,
      errors: [],
      warnings: [],
      sanitizedFileName: '',
      detectedMimeType: '',
      fileSize: 0
    };

    try {
      // Basic file checks
      if (!file || !(file instanceof File)) {
        result.errors.push('Invalid file object');
        return result;
      }

      result.fileSize = file.size;
      const originalName = file.name || 'unknown';
      
      // 1. File name validation and sanitization
      const nameValidation = this.validateFileName(originalName);
      if (!nameValidation.isValid) {
        result.errors.push(...nameValidation.errors);
      }
      result.sanitizedFileName = nameValidation.sanitizedName;

      // 2. File extension validation
      const extension = this.getFileExtension(originalName);
      if (this.dangerousExtensions.has(extension)) {
        result.errors.push(`Dangerous file extension detected: .${extension}`);
        return result;
      }

      if (!this.allowedExtensions.has(extension)) {
        result.errors.push(`File extension .${extension} is not allowed`);
        return result;
      }

      // 3. MIME type validation
      const mimeValidation = await this.validateMimeType(file);
      if (!mimeValidation.isValid) {
        result.errors.push(...mimeValidation.errors);
        return result;
      }
      result.detectedMimeType = mimeValidation.detectedMime;

      // 4. File content validation (magic number check)
      const contentValidation = await this.validateFileContent(file, mimeValidation.detectedMime);
      if (!contentValidation.isValid) {
        result.errors.push(...contentValidation.errors);
      }

      // 5. File size validation
      const sizeValidation = this.validateFileSize(file, mimeValidation.detectedMime);
      if (!sizeValidation.isValid) {
        result.errors.push(...sizeValidation.errors);
      }

      // 6. Additional security checks
      const securityValidation = await this.performSecurityChecks(file);
      if (!securityValidation.isValid) {
        result.errors.push(...securityValidation.errors);
      }
      if (securityValidation.warnings.length > 0) {
        result.warnings.push(...securityValidation.warnings);
      }

      result.isValid = result.errors.length === 0;
      return result;

    } catch (error) {
      result.errors.push(`File validation failed: ${error.message}`);
      return result;
    }
  }

  /**
   * Validate and sanitize file name
   * @param {string} fileName - Original file name
   * @returns {Object} - Validation result with sanitized name
   */
  validateFileName(fileName) {
    const result = { isValid: true, errors: [], sanitizedName: '' };
    
    if (!fileName || typeof fileName !== 'string') {
      result.errors.push('Invalid file name');
      result.isValid = false;
      return result;
    }

    // Check for path traversal attempts
    if (fileName.includes('../') || fileName.includes('..\\') || fileName.includes('/') || fileName.includes('\\')) {
      result.errors.push('File name contains invalid path characters');
      result.isValid = false;
    }

    // Check for null bytes and control characters (C0: U+0000-U+001F, DEL+C1: U+007F-U+009F)
    if (/[\u0000-\u001f\u007f-\u009f]/.test(fileName)) { // NOSONAR
      result.errors.push('File name contains invalid control characters');
      result.isValid = false;
    }

    // Sanitize file name
    let sanitized = fileName
      .replace(/[<>:"/\\|?*\u0000-\u001f\u007f-\u009f]/g, '') // NOSONAR
      .replace(/^\.+/, '')
      .substring(0, 100);

    // Ensure file has extension
    if (!sanitized.includes('.')) {
      sanitized += '.txt';
    }

    // Prevent reserved Windows names
    const reservedNames = ['CON', 'PRN', 'AUX', 'NUL', 'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9', 'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'];
    const nameWithoutExt = sanitized.split('.')[0].toUpperCase();
    if (reservedNames.includes(nameWithoutExt)) {
      sanitized = `file_${sanitized}`;
    }

    result.sanitizedName = sanitized;
    return result;
  }

  /**
   * Get file extension safely
   * @param {string} fileName - File name
   * @returns {string} - Lowercase file extension
   */
  getFileExtension(fileName) {
    if (!fileName || typeof fileName !== 'string') return '';
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
  }

  /**
   * Validate MIME type
   * @param {File} file - File object
   * @returns {Promise<Object>} - MIME validation result
   */
  async validateMimeType(file) {
    const result = { isValid: false, errors: [], detectedMime: '' };

    try {
      // Get MIME type from file object
      const browserMime = file.type;
      result.detectedMime = browserMime;

      // Check if MIME type is in whitelist
      if (!this.allowedMimeTypes.has(browserMime)) {
        result.errors.push(`MIME type '${browserMime}' is not allowed`);
        return result;
      }

      // Verify extension matches MIME type
      const extension = this.getFileExtension(file.name);
      const expectedMimes = this.getExpectedMimeTypes(extension);
      
      if (expectedMimes.length > 0 && !expectedMimes.includes(browserMime)) {
        result.errors.push(`MIME type '${browserMime}' doesn't match file extension '.${extension}'`);
        return result;
      }

      result.isValid = true;
      return result;

    } catch (error) {
      result.errors.push(`MIME type validation failed: ${error.message}`);
      return result;
    }
  }

  /**
   * Get expected MIME types for file extension
   * @param {string} extension - File extension
   * @returns {Array<string>} - Array of valid MIME types
   */
  getExpectedMimeTypes(extension) {
    const mimeMap = {
      'jpg': ['image/jpeg'],
      'jpeg': ['image/jpeg'],
      'png': ['image/png'],
      'gif': ['image/gif'],
      'webp': ['image/webp'],
      'bmp': ['image/bmp'],
      'pdf': ['application/pdf'],
      'doc': ['application/msword'],
      'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      'xls': ['application/vnd.ms-excel'],
      'xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      'txt': ['text/plain'],
      'csv': ['text/csv']
    };
    
    return mimeMap[extension] || [];
  }

  /**
   * Validate file content using magic numbers
   * @param {File} file - File object
   * @param {string} expectedMime - Expected MIME type
   * @returns {Promise<Object>} - Content validation result
   */
  async validateFileContent(file, expectedMime) {
    const result = { isValid: true, errors: [] };

    try {
      // Read first 16 bytes for magic number checking
      const arrayBuffer = await this.readFileBytes(file, 16);
      const bytes = new Uint8Array(arrayBuffer);

      // Check magic numbers if available for this MIME type
      const magicSignatures = this.magicNumbers[expectedMime];
      if (magicSignatures) {
        const isValidMagic = magicSignatures.some(signature => 
          this.matchesMagicNumber(bytes, signature)
        );

        if (!isValidMagic) {
          // For JPEG files, be more lenient - many valid JPEG files don't start with exact FF D8 FF
          if (expectedMime === 'image/jpeg' && (bytes[0] === 0xFF && bytes[1] === 0xD8)) {
            // Valid JPEG - starts with FF D8 which is the JPEG marker
            result.isValid = true;
          } else if (expectedMime === 'image/jpeg') {
            // For screenshot files and other JPEG variants, skip magic number validation
            console.warn(`JPEG file ${file.name} has non-standard header but allowing as JPEG`);
            result.isValid = true;
          } else {
            result.errors.push(`File content doesn't match expected format for ${expectedMime}`);
            result.isValid = false;
          }
        }
      }

      // Additional content-based checks
      await this.performContentSecurityChecks(file, bytes, result);

      return result;

    } catch (error) {
      result.errors.push(`Content validation failed: ${error.message}`);
      result.isValid = false;
      return result;
    }
  }

  /**
   * Read first N bytes of file
   * @param {File} file - File object
   * @param {number} numBytes - Number of bytes to read
   * @returns {Promise<ArrayBuffer>} - File bytes
   */
  readFileBytes(file, numBytes) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const blob = file.slice(0, numBytes);
      
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(blob);
    });
  }

  /**
   * Check if bytes match magic number signature
   * @param {Uint8Array} bytes - File bytes
   * @param {Array} signature - Magic number signature
   * @returns {boolean} - True if matches
   */
  matchesMagicNumber(bytes, signature) {
    for (let i = 0; i < signature.length; i++) {
      if (signature[i] !== null && bytes[i] !== signature[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Validate file size
   * @param {File} file - File object
   * @param {string} mimeType - MIME type
   * @returns {Object} - Size validation result
   */
  validateFileSize(file, mimeType) {
    const result = { isValid: true, errors: [] };
    
    const maxSize = this.maxFileSizes[mimeType] || (1 * 1024 * 1024); // Default 1MB
    
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      result.errors.push(`File size ${(file.size / (1024 * 1024)).toFixed(1)}MB exceeds maximum allowed ${maxSizeMB}MB`);
      result.isValid = false;
    }

    if (file.size === 0) {
      result.errors.push('File is empty');
      result.isValid = false;
    }

    return result;
  }

  /**
   * Perform additional security checks
   * @param {File} file - File object
   * @returns {Promise<Object>} - Security check results
   */
  async performSecurityChecks(file) {
    const result = { isValid: true, errors: [], warnings: [] };

    try {
      // Check for suspicious file patterns
      const fileContent = await this.readFileAsText(file, 1024); // Read first 1KB as text
      
      // Check for script injections in file content
      const scriptPatterns = [
        /<script\b/i, /javascript:/i, /vbscript:/i, /onload\s*=/i,
        /onerror\s*=/i, /onclick\s*=/i, /eval\s*\(/i, /document\./i,
        /window\./i, /<iframe\b/i, /<object\b/i, /<embed\b/i
      ];

      for (const pattern of scriptPatterns) {
        if (pattern.test(fileContent)) {
          result.errors.push('File contains potentially malicious script content');
          result.isValid = false;
          break;
        }
      }

      // Check for polyglot files (files that are valid in multiple formats)
      if (this.isPolyglotFile(fileContent)) {
        result.warnings.push('File may be a polyglot file - exercise caution');
      }

      // Check for embedded executables
      if (this.containsExecutableSignatures(fileContent)) {
        result.errors.push('File contains embedded executable content');
        result.isValid = false;
      }

    } catch (error) {
      // If we can't read as text, it's likely binary - that's okay
      console.debug('Security check text reading failed (expected for binary files):', error.message);
    }

    return result;
  }

  /**
   * Read file as text (for content analysis)
   * @param {File} file - File object
   * @param {number} maxBytes - Maximum bytes to read
   * @returns {Promise<string>} - File content as text
   */
  readFileAsText(file, maxBytes = 1024) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const blob = file.slice(0, maxBytes);
      
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file as text'));
      reader.readAsText(blob);
    });
  }

  /**
   * Perform content-based security checks
   * @param {File} file - File object
   * @param {Uint8Array} bytes - File bytes
   * @param {Object} result - Result object to update
   */
  async performContentSecurityChecks(file, bytes, result) {
    // Check for ZIP-based file format exploits
    if (this.isZipBased(bytes)) {
      // Additional checks for Office documents and other ZIP-based files
      await this.validateZipBasedFile(file, result);
    }

    // Check for image-based exploits
    if (file.type.startsWith('image/')) {
      this.validateImageFile(bytes, result);
    }
  }

  /**
   * Check if file is ZIP-based
   * @param {Uint8Array} bytes - File bytes
   * @returns {boolean} - True if ZIP-based
   */
  isZipBased(bytes) {
    return this.matchesMagicNumber(bytes, [0x50, 0x4B, 0x03, 0x04]);
  }

  /**
   * Validate ZIP-based files (Office docs, etc.)
   * @param {File} file - File object
   * @param {Object} result - Result object to update
   */
  async validateZipBasedFile(file, result) {
    // ZIP bombs and other ZIP-based attacks are primarily server-side concerns
    // Client-side we can check basic structure
    if (file.size > 50 * 1024 * 1024) { // 50MB limit for ZIP-based files
      result.errors.push('ZIP-based file is too large and may be a ZIP bomb');
    }
  }

  /**
   * Validate image files for common exploits
   * @param {Uint8Array} bytes - File bytes
   * @param {Object} result - Result object to update
   */
  validateImageFile(bytes, result) {
    // Check for common image exploit patterns
    const suspiciousPatterns = [
      [0x3C, 0x73, 0x63, 0x72, 0x69, 0x70, 0x74], // <script
      [0x6A, 0x61, 0x76, 0x61, 0x73, 0x63, 0x72, 0x69, 0x70, 0x74], // javascript
    ];

    for (const pattern of suspiciousPatterns) {
      if (this.findBytesPattern(bytes, pattern)) {
        result.errors.push('Image file contains suspicious script content');
        break;
      }
    }
  }

  /**
   * Find byte pattern in file
   * @param {Uint8Array} bytes - File bytes
   * @param {Array} pattern - Pattern to find
   * @returns {boolean} - True if pattern found
   */
  findBytesPattern(bytes, pattern) {
    for (let i = 0; i <= bytes.length - pattern.length; i++) {
      let found = true;
      for (let j = 0; j < pattern.length; j++) {
        if (bytes[i + j] !== pattern[j]) {
          found = false;
          break;
        }
      }
      if (found) return true;
    }
    return false;
  }

  /**
   * Check if file is potentially a polyglot
   * @param {string} content - File content as string
   * @returns {boolean} - True if potentially polyglot
   */
  isPolyglotFile(content) {
    // Simple heuristic for polyglot detection
    const indicators = [
      content.includes('<?php'), // PHP in image
      content.includes('<script'), // JavaScript in image
      content.includes('<%'), // JSP/ASP in image
      /GIF89a.*<script/i.test(content), // Script in GIF
    ];
    
    return indicators.some(indicator => indicator);
  }

  /**
   * Check for embedded executable signatures
   * @param {string} content - File content as string
   * @returns {boolean} - True if executable content found
   */
  containsExecutableSignatures(content) {
    const executablePatterns = [
      /MZ/,  // Windows PE header
      /\x7fELF/, // Linux ELF header
      /\xca\xfe\xba\xbe/, // Java class file
    ];
    
    return executablePatterns.some(pattern => pattern.test(content));
  }

  /**
   * Batch validate multiple files
   * @param {Array<File>} files - Array of files to validate
   * @returns {Promise<Array<Object>>} - Array of validation results
   */
  async validateFiles(files) {
    const results = [];
    
    for (const file of files) {
      const result = await this.validateFile(file);
      results.push({
        file: file,
        validation: result
      });
    }
    
    return results;
  }

  /**
   * Get security configuration summary
   * @returns {Object} - Current security configuration
   */
  getSecurityConfig() {
    return {
      allowedMimeTypes: Array.from(this.allowedMimeTypes),
      allowedExtensions: Array.from(this.allowedExtensions),
      dangerousExtensions: Array.from(this.dangerousExtensions),
      maxFileSizes: { ...this.maxFileSizes },
      securityFeatures: [
        'MIME type validation',
        'File extension validation',
        'Magic number verification',
        'File size limits',
        'Content security scanning',
        'Path traversal protection',
        'Polyglot detection',
        'Script injection detection'
      ]
    };
  }
}

// Export singleton instance
export const fileUploadSecurity = new FileUploadSecurityService();
export default fileUploadSecurity;